import React, { useCallback, useState, useEffect, useRef } from 'react'
import { useRouter } from 'expo-router'
import { useAction, useMutation, useQuery } from 'convex/react'

import { SittingOutModal } from '@/screens/GameContainer/Modals/SittingOutModal'
import { QuickGameOverModal } from '@/screens/GameContainer/Modals/QuickGameOverModal'
import { TournamentGameOverModal } from '@/screens/GameContainer/Modals/TournamentGameOverModal'
import { BuyChip } from '@/components/ANewDesign/ModalContent/Content/BuyChip'
import { EndPracticeGame } from '@/components/ANewDesign/ModalContent/Content/EndPracticeGame'
import { Daily10Results } from '@/components/ANewDesign/ModalContent/Content/Daily10Results/Daily10Results'
import { ConfirmOption } from '@/components/ANewDesign/BottomModalContent/Content'
import { GameModal } from '@/screens/GameContainer/Modals/GameModalContainer'
import { toastNotificationStore } from '@/components/ANewDesign/ToastNotification/ToastNotification'
import { api } from '@ppwr/convex/_generated/api'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'
import { REBUY_LIMIT_REACHED_ERROR_CODE } from '@ppwr/convex/games/errors'
import { GAME_CONFIG } from '@ppwr/poker'
import { useGameContext } from './GameContext'
import { useGameState } from './useGameState'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { translate } from '../../i18n'
import { isConvexErrorWithCode } from '../../utils/errors/convex'
import { useActiveFlowSchedule } from '../../hooks/convex/useActiveFlowSchedule'
import { useMyTournamentTable } from '@/screens/tournament/useMyTournamentTable'
import {
  redirectToLeaderboard,
  redirectToLobby,
  redirectToNewTable,
  redirectToSummary
} from '@/screens/tournament/mttNavigation'

/**
 * Post-move toast. Fires inline as part of the redirect so it doesn't depend
 * on HUD continuity (the HUD on the source game unmounts before the new HUD
 * mounts on the destination, so a continuity-based detector never matches).
 *
 * Falls back to a generic message if `tableNumber` is null/0 (rare; can
 * happen if Convex broadcasts land out-of-order at the moment of redirect).
 */
function announceTableMoveComplete(tableNumber: number | null | undefined) {
  const text = tableNumber && tableNumber > 0
    ? `Table move complete. You are now at Table ${tableNumber}.`
    : 'Table move complete. You are now at a new table.'
  toastNotificationStore.getState().show({ text })
}

/**
 * Surface an error to the user via the toast queue and log to the console
 * for dev triage. Replaces the prior `console.error(...)` pattern that was
 * invisible on native — busted mid-table rebuys / practice continues /
 * sitting-out returns all failed silently, leaving users staring at an
 * unresponsive modal.
 */
function reportMutationError(label: string, error: unknown) {
  console.error(`${label}:`, error)
  const message = error instanceof Error ? error.message : label
  toastNotificationStore.getState().show({ text: message })
}

type TournamentEndDetails = {
  title: string
  message: string
  buttonLabel: string
}

// Daily10 constants
const { DEFAULT_FREE_PREFLOP_FOLDS } = GAME_CONSTANTS.DAILY10_CONSTANTS

// ---------------------------------------------------------------------------
// Hooks – keep business logic out of the render tree
// ---------------------------------------------------------------------------

/**
 * Handlers for mid-table rebuy flows (private/public games). Decline uses same leave + navigate as sitting-out.
 *
 * Current behavior (PP-4881): decline does NOT leave the game. It only opens the Game Over screen; the
 * player stays seated as `awaiting_rebuy`. Whether decline should leave (and free the seat) is an open
 * question — see packages/convex/docs/REBUY_FLOW.md, "Known gaps / follow-ups".
 */
function useMidTableRebuyActions(onDeclined: () => void | Promise<void>) {
  const { myPlayer, game } = useGameState()
  const { userId } = useGameContext()
  const acceptMidTableRebuyMutation = useMutation(api.games.mutations.acceptMidTableRebuy)

  const amount = GAME_CONFIG.DEFAULT_REBUY_AMOUNT_FOR_QUICK_GAME

  const handleMidTableRebuyAccept = useCallback(async () => {
    if (!(myPlayer?._id && game?._id && userId)) return
    try {
      await acceptMidTableRebuyMutation({ gameId: game._id, userId, amount })
    } catch (error) {
      console.error('Failed to accept mid-table rebuy:', error)
      const isRebuyLimitError = isConvexErrorWithCode(error, REBUY_LIMIT_REACHED_ERROR_CODE)
      toastNotificationStore.getState().show({
        text: translate(isRebuyLimitError ? 'common.rebuyLimitReached' : 'common.somethingWentWrong')
      })
    }
  }, [myPlayer?._id, game?._id, userId, acceptMidTableRebuyMutation, amount])

  const handleMidTableRebuyDecline = useCallback(() => {
    Promise.resolve(onDeclined()).catch((error) => {
      reportMutationError('Failed to decline rebuy. Please try again.', error)
    })
  }, [onDeclined])

  return { handleMidTableRebuyAccept, handleMidTableRebuyDecline }
}

/** Handlers for practice-game rebuy / win-continue / decline flows. */
function usePracticeGameActions() {
  const { myPlayer, game } = useGameState()
  const router = useRouter()
  const rebuyAndResumeAction = useAction(api.games.actions.rebuyAndResumeGame)
  const continueAfterWinAction = useAction(api.games.actions.continueAfterWin)
  const endGameMutation = useMutation(api.games.mutations.endGame)
  const {
    analytics: { setEvent }
  } = useStore()

  const amount = GAME_CONFIG.DEFAULT_REBUY_AMOUNT_FOR_QUICK_GAME

  const handleRebuyAndResume = useCallback(async () => {
    if (!(myPlayer?._id && game?._id)) return
    try {
      await rebuyAndResumeAction({ gameId: game._id, playerId: myPlayer._id, amount })
    } catch (error) {
      reportMutationError('Failed to rebuy. Please try again.', error)
    }
  }, [myPlayer?._id, game?._id, rebuyAndResumeAction, amount])

  const handleContinueAfterWin = useCallback(async () => {
    if (!game?._id) return
    try {
      await continueAfterWinAction({ gameId: game._id, amount })
    } catch (error) {
      reportMutationError('Failed to resume after win. Please try again.', error)
    }
  }, [game?._id, continueAfterWinAction, amount])

  const handleDeclineAndExit = useCallback(async () => {
    if (!game?._id) return
    try {
      await endGameMutation({ gameId: game._id })
      setEvent(events.gameStats.post_game_stats_screen_exited, {
        game_state: 'post game',
        isConvexEnabled: true
      })
      router.replace({ pathname: '/game/statistics', params: { gameId: game._id } })
    } catch (error) {
      reportMutationError('Failed to end game. Please try again.', error)
    }
  }, [game?._id, endGameMutation, router, setEvent])

  return { handleRebuyAndResume, handleContinueAfterWin, handleDeclineAndExit }
}

/** Handlers for sitting-out flow (request return / leave). */
function useSittingOutActions() {
  const { myPlayer, game } = useGameState()
  const router = useRouter()
  const requestReturnToGameMutation = useMutation(api.games.mutations.requestReturnToGame)
  const leaveGameMutation = useMutation(api.games.mutations.leaveGame)

  /** Returns true when the return request succeeded. */
  const handleReturnToGame = useCallback(async (): Promise<boolean> => {
    const gameId = game?._id
    const playerId = myPlayer?._id
    if (!(gameId && playerId)) return false
    try {
      await requestReturnToGameMutation({ gameId, playerId })
      return true
    } catch (error) {
      reportMutationError('Failed to return to game. Please try again.', error)
      return false
    }
  }, [game?._id, myPlayer?._id, requestReturnToGameMutation])

  const handleLeaveFromSittingOut = useCallback(async () => {
    const gameId = game?._id
    const userId = myPlayer?.userId
    const loungeId = game?.loungeId
    if (!(gameId && userId)) return
    const isTerminal = game?.state === 'finished' || game?.state === 'deleted'
    flagVoluntaryLeave(gameId)
    try {
      await leaveGameMutation({ gameId, userId })
      if (!isTerminal && loungeId) {
        router.dismissTo({ pathname: '/(root)/lounges/[loungeId]', params: { loungeId } } as any)
      } else {
        router.dismissTo('/(root)/(tabs)/play')
      }
    } catch (error) {
      reportMutationError('Failed to leave game. Please try again.', error)
    }
  }, [game, myPlayer?.userId, leaveGameMutation, router])

  return { handleReturnToGame, handleLeaveFromSittingOut }
}

// Module-level set of gameIds the user is voluntarily leaving.
// Prevents useRemovedFromGame from misidentifying a voluntary leave as a teacher removal.
const voluntaryLeaveGameIds = new Set<string>()
export function flagVoluntaryLeave(gameId: string) {
  voluntaryLeaveGameIds.add(gameId)
  setTimeout(() => voluntaryLeaveGameIds.delete(gameId), 3000)
}

/**
 * Detect when the current user was removed from the game.
 *
 * Three outcomes:
 *  1. Tournament table move: the player's `myTournamentTable.gameId`
 *     points at a different game. We `replace` directly into the new
 *     game once any in-flight showdown/winner reveal has cleared. The
 *     lobby is NOT a transport screen.
 *  2. Tournament bust-out: `tournamentEliminated` is true. Replace into
 *     the per-user summary screen.
 *  3. Non-tournament removal (e.g. teacher kick): trigger the Game Over
 *     modal, then exit navigates to the Home tab. Ignores voluntary leaves
 *     and seat→teacher role switches (user becomes teacherUserId).
 */
function useRemovedFromGame() {
  const { userId } = useGameContext()
  const { myPlayer, game } = useGameState()
  const activeFlowSchedule = useActiveFlowSchedule(game?._id, false)
  const router = useRouter()
  const [wasRemovedFromGame, setWasRemovedFromGame] = useState(false)
  const hadPlayerInGameRef = useRef(false)
  /**
   * Pending table-move target. Held while the source table's reveal animations
   * finish. Captures the destination table number at staging time so the
   * post-move toast renders the correct label even if `myTournamentTable`
   * shifts between the deferred capture and the flush.
   */
  const pendingTableMoveRef = useRef<{
    newGameId: string
    destinationTableNumber: number | null
  } | null>(null)
  const myTournamentTable = useMyTournamentTable(game?.tournamentId ?? undefined)

  // Reduce the effect deps to SCALARS (was: full `activeFlowSchedule` object,
  // full `myTournamentTable` object — both new references on every Convex
  // push regardless of whether the fields we care about changed).
  const isShowdownReveal = activeFlowSchedule?.isShowdownReveal ?? false
  const isWinnersReveal = activeFlowSchedule?.isWinnersReveal ?? false
  const tournamentEliminated = !!myTournamentTable?.tournamentEliminated
  const tournamentPendingReentry = !!myTournamentTable?.pendingReentry
  const tournamentTableGameId = myTournamentTable?.gameId ?? null
  const tournamentTableNumber = myTournamentTable?.tableNumber ?? null
  const gameId = game?._id ?? null
  const gameMode = game?.mode ?? null
  const tournamentId = game?.tournamentId ?? null
  const teacherUserId = game?.teacherUserId ?? null
  const hasMyPlayer = !!myPlayer

  // Clear any pending table move whenever the user lands on a different game
  // route (post-redirect). Without this, a ref left over from a previous game
  // could flush against the wrong destination if the user navigates back.
  useEffect(() => {
    pendingTableMoveRef.current = null
  }, [gameId])

  // Flush a pending table move once the source table's reveal completes.
  useEffect(() => {
    if (pendingTableMoveRef.current && !isWinnersReveal && !isShowdownReveal) {
      const { newGameId, destinationTableNumber } = pendingTableMoveRef.current
      pendingTableMoveRef.current = null
      announceTableMoveComplete(destinationTableNumber)
      redirectToNewTable(router, newGameId)
    }
  }, [isShowdownReveal, isWinnersReveal, router])


  // Tournament bust-out WITH a pending reentry: the player row stays alive
  // (chips 0, pendingReentry=true) so the reentry modal can resolve, which
  // means the `!hasMyPlayer` redirect below never fires and the player would
  // otherwise sit at the table watching bots with no prompt. The reentry
  // modal itself only mounts at the MTT route layout (`/mtt/[tournamentId]`),
  // so route there — the layout mounts `TournamentReentryPrompt` on the
  // summary tab and the modal is waiting when they arrive.
  useEffect(() => {
    if (tournamentPendingReentry && gameMode === 'mtt' && tournamentId) {
      redirectToSummary(router, tournamentId)
    }
    // gameId intentionally omitted: the redirect should only fire on the
    // pendingReentry transition, not re-fire when the server assigns a new
    // gameId on reentry.
  }, [tournamentPendingReentry, gameMode, tournamentId, router])
  useEffect(() => {
    return () => {
      pendingTableMoveRef.current = null
    }
  }, [])

  useEffect(() => {
    if (hasMyPlayer && gameId) {
      hadPlayerInGameRef.current = true
      // If we landed on a new game route (post table-move), clear any pending redirect.
      pendingTableMoveRef.current = null
    }
    if (hadPlayerInGameRef.current && gameId && !hasMyPlayer) {
      if (voluntaryLeaveGameIds.has(gameId)) {
        return
      }
      // Don't gate on `hostBotDecisionPending` here. The prompt is mounted
      // only at the MTT route layout (`/mtt/[tournamentId]/_layout.tsx`),
      // not at `/game/[gameId]` — a sibling route. Blocking the redirect
      // would leave the busted host stuck on the game screen with no way
      // for the prompt to render. Redirect to the summary tab; the MTT
      // layout will mount and `TournamentHostBotPrompt` will pick up
      // `myTable.hostBotDecision` and show the modal there.
      hadPlayerInGameRef.current = false
      if (gameMode === 'mtt' && tournamentId) {
        // Bust-out: player row was deleted (no-rebuy bust, or post-decline). The
        // tournament-eliminated flag on the server query is the signal — route to
        // the bust-out summary screen instead of treating this as a table move.
        if (tournamentEliminated) {
          redirectToSummary(router, tournamentId)
          return
        }
        // Table move: the player is still active and the server has assigned a
        // new gameId. Wait until any showdown/winner reveal on the source table
        // has finished before replacing, otherwise the final reveal frames are
        // truncated.
        if (tournamentTableGameId && tournamentTableGameId !== gameId) {
          const revealStillShowing = isShowdownReveal || isWinnersReveal
          if (revealStillShowing) {
            pendingTableMoveRef.current = {
              newGameId: tournamentTableGameId,
              destinationTableNumber: tournamentTableNumber
            }
          } else {
            announceTableMoveComplete(tournamentTableNumber)
            redirectToNewTable(router, tournamentTableGameId)
          }
        }
        // If tournamentTableGameId is not yet known, the server hasn't finished
        // assignment. The next render with an updated row will retrigger this
        // effect via the gameId dependency.
      } else {
        // Seat→teacher role switch is not a removal — the user stays in the
        // game as its teacher (teacherUserId), so don't show the Game Over modal.
        const switchedToTeacher = teacherUserId === userId
        if (!switchedToTeacher) {
          setWasRemovedFromGame(true)
        }
      }
    }
  }, [
    hasMyPlayer,
    gameId,
    gameMode,
    tournamentId,
    router,
    isShowdownReveal,
    isWinnersReveal,
    tournamentEliminated,
    tournamentTableGameId,
    tournamentTableNumber,
    teacherUserId,
    userId
  ])

  const dismiss = useCallback(() => setWasRemovedFromGame(false), [])
  return { wasRemovedFromGame, dismissRemovedFromGame: dismiss }
}

/**
 * Detect when the host ends the game while it was still in progress.
 * Returns true once when the transition is detected.
 */
function useGameEndedByHost() {
  const { game, isHost } = useGameState()
  const [gameEndedByHost, setGameEndedByHost] = useState(false)
  const previousGameStateRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const currentState = game?.state
    const previousState = previousGameStateRef.current

    if (
      previousState &&
      (previousState === 'pregame' || previousState === 'playing') &&
      (currentState === 'deleted' || currentState === 'finished') &&
      !isHost &&
      game?.code
    ) {
      setGameEndedByHost(true)
    }

    previousGameStateRef.current = currentState
  }, [game?.state, game?.code, isHost])

  const dismiss = useCallback(() => setGameEndedByHost(false), [])

  return { gameEndedByHost, dismissGameEndedByHost: dismiss }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GameModals = () => {
  const { myPlayer, game, players } = useGameState()
  const { userId } = useGameContext()
  const router = useRouter()
  const {
    analytics: { setEvent }
  } = useStore()

  const myTournamentTable = useMyTournamentTable(game?.tournamentId ?? undefined)
  const tournamentStatus = useQuery(
    api.tournaments.queries.getTournamentStatus,
    game?.tournamentId
      ? {
          tournamentId: game.tournamentId
        }
      : 'skip'
  )

  // --- State ---
  const [quickGameOverVisible, setQuickGameOverVisible] = useState(false)
  const [hasRequestedReturnToGame, setHasRequestedReturnToGame] = useState(false)
  const [tournamentEndDetails, setTournamentEndDetails] = useState<TournamentEndDetails>({
    title: 'Tournament Finished',
    message: 'The tournament has ended. Review the results before returning to the lobby.',
    buttonLabel: 'View Results'
  })

  const onDeclineMidTableRebuy = useCallback(() => setQuickGameOverVisible(true), [])

  // --- Custom hooks ---
  const { handleRebuyAndResume, handleContinueAfterWin, handleDeclineAndExit } = usePracticeGameActions()
  const { handleReturnToGame: baseHandleReturnToGame, handleLeaveFromSittingOut } = useSittingOutActions()
  const { handleMidTableRebuyAccept, handleMidTableRebuyDecline } = useMidTableRebuyActions(onDeclineMidTableRebuy)
  const { gameEndedByHost, dismissGameEndedByHost } = useGameEndedByHost()
  const { wasRemovedFromGame, dismissRemovedFromGame } = useRemovedFromGame()

  // Wrap return-to-game to also track the local "requested" flag.
  // Set it only on success, so a failed attempt leaves "Return to Game" visible (PP-4878).
  // MTT keeps setting it either way: the flag is what stops the auto-return below from repeating.
  const isTournament = game?.mode === 'mtt'
  const handleReturnToGame = useCallback(async () => {
    const succeeded = await baseHandleReturnToGame()
    if (succeeded || isTournament) {
      setHasRequestedReturnToGame(true)
    }
  }, [baseHandleReturnToGame, isTournament])

  // Reset "requested return" when player is no longer sitting out
  useEffect(() => {
    if (myPlayer?.state !== 'sittingout') {
      setHasRequestedReturnToGame(false)
    }
  }, [myPlayer?.state])

  // --- Derived visibility ---
  const isGameFinished = game?.state === 'finished'
  const isPracticeGame = game?.mode === 'practice'
  const isDaily10 = game?.mode === 'daily10'
  const isPrivateOrPublic = game?.mode === 'private' || game?.mode === 'public'
  const hasNoChips = myPlayer?.chips === 0
  const hasChips = (myPlayer?.chips ?? 0) > 0

  const showMidTableRebuy =
    game?.state === 'playing' && isPrivateOrPublic && myPlayer?.kind === 'human' && myPlayer?.state === 'awaiting_rebuy'

  const showDaily10Results = !!(isDaily10 && isGameFinished)
  const daily10Status = useQuery(
    api.games.queries.getDaily10Status,
    showDaily10Results && myPlayer?.userId && game?.playedDate
      ? { userId: myPlayer.userId, playedDate: game.playedDate }
      : 'skip'
  )
  const streak = (daily10Status?.status === 'completed' ? daily10Status.streak : undefined) ?? 1
  const showBuyChip = !!(isGameFinished && hasNoChips && isPracticeGame)

  const allOtherPlayersHaveNoChips =
    !!myPlayer && players.filter((p) => p._id !== myPlayer._id).every((p) => p.chips === 0)
  const showEndPractice = !!(isGameFinished && hasChips && allOtherPlayersHaveNoChips && isPracticeGame)

  // Matches StartGameSheet logic (almost) but adds whether player has requested a return in this sheet
  const isSittingOut = myPlayer?.state === 'sittingout' && game?.state === 'playing'
  const showSittingOut = isSittingOut && !hasRequestedReturnToGame && !isTournament

  // Tournament: auto-activate sitting-out players when they arrive at the table
  useEffect(() => {
    if (isSittingOut && isTournament && !hasRequestedReturnToGame) {
      handleReturnToGame()
    }
  }, [isSittingOut, isTournament, hasRequestedReturnToGame, handleReturnToGame])

  const isQuickGameFinished = isGameFinished && !isPracticeGame && !isDaily10 && !isTournament
  const isTournamentGameFinished = !!(isTournament && isGameFinished && game?.tournamentId)
  const hasPendingHostBotDecision = !!myTournamentTable?.hostBotDecision
  const shouldShowTournamentEndModal = !!(
    isTournamentGameFinished &&
    tournamentStatus?.state === 'finished' &&
    !hasPendingHostBotDecision
  )

  // Show tournament game over modal (delayed so the final hand result is visible)
  const [tournamentGameOverVisible, setTournamentGameOverVisible] = useState(false)
  const previousTournamentGameIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (previousTournamentGameIdRef.current !== game?._id) {
      previousTournamentGameIdRef.current = game?._id
      setTournamentGameOverVisible(false)
    }
  }, [game?._id])

  useEffect(() => {
    if (!isTournamentGameFinished) return

    if (tournamentStatus?.state === 'finished') {
      setTournamentEndDetails({
        title: 'Tournament Finished',
        message: 'You were the last human player remaining. The tournament has ended and final results are ready.',
        buttonLabel: 'View Results'
      })
      return
    }

    setTournamentEndDetails({
      title: 'Table Finished',
      message: 'This tournament table has finished. Return to the lobby to continue following the tournament.',
      buttonLabel: 'Return to Lobby'
    })
  }, [isTournamentGameFinished, tournamentStatus?.state])

  // Re-check the host-bot prompt state at timer-fire (not just at scheduling
  // time). The pending flag can flip true between when the timer was
  // scheduled and when it fires — if we don't re-check, the game-over modal
  // opens on top of the host-bot prompt and the user sees stacked modals.
  // Capturing the flag in a ref lets the timer body read the current value
  // without re-running the effect every time the flag toggles.
  const hostBotDecisionPendingRef = useRef(hasPendingHostBotDecision)
  useEffect(() => {
    hostBotDecisionPendingRef.current = hasPendingHostBotDecision
  }, [hasPendingHostBotDecision])

  useEffect(() => {
    if (isTournamentGameFinished && !tournamentGameOverVisible && !hasPendingHostBotDecision) {
      const timer = setTimeout(() => {
        if (hostBotDecisionPendingRef.current) return // race: host-bot prompt opened during the delay
        setTournamentGameOverVisible(true)
      }, GAME_CONSTANTS.WINNERS_REVEAL_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isTournamentGameFinished, tournamentGameOverVisible, hasPendingHostBotDecision])

  const handleTournamentGameOver = useCallback(() => {
    if (!game?.tournamentId) return
    setTournamentGameOverVisible(false)
    if (shouldShowTournamentEndModal) {
      // Tournament has concluded — replace into the leaderboard with a flag
      // so it can show the "tournament complete" toast.
      redirectToLeaderboard(router, game.tournamentId, { justFinished: '1' })
    } else {
      // Table finished but tournament still going (rare; usually the player
      // is moved before this fires). Send them back to lobby as a clean
      // state-machine reset.
      redirectToLobby(router, game.tournamentId)
    }
  }, [game?.tournamentId, router, shouldShowTournamentEndModal])

  // Show quick game over modal when a non-practice game finishes
  useEffect(() => {
    if (isQuickGameFinished && game?._id && !quickGameOverVisible) {
      setQuickGameOverVisible(true)
    }
  }, [isQuickGameFinished, game?._id, quickGameOverVisible])

  // Removed players and host-ended games also show the Game Over modal
  const showQuickGameOver = quickGameOverVisible || gameEndedByHost || wasRemovedFromGame

  // --- Analytics: fire once when a game-end modal becomes visible ---
  const hasTrackedGameEndRef = useRef(false)
  useEffect(() => {
    if (
      (showBuyChip || showMidTableRebuy || showEndPractice || showQuickGameOver || showDaily10Results) &&
      !hasTrackedGameEndRef.current
    ) {
      hasTrackedGameEndRef.current = true
      setEvent(events.gameStats.game_stats_button_viewed, { isConvexEnabled: true })
    }
  }, [showBuyChip, showMidTableRebuy, showEndPractice, showQuickGameOver, showDaily10Results, setEvent])

  // --- Handlers ---
  const dismissQuickGameOver = useCallback(() => {
    setQuickGameOverVisible(false)
    dismissGameEndedByHost()
    dismissRemovedFromGame()
  }, [dismissGameEndedByHost, dismissRemovedFromGame])

  const handleQuickGameOverViewStats = useCallback(() => {
    if (!game?._id) return
    dismissQuickGameOver()
    router.replace({ pathname: '/game/statistics', params: { gameId: game._id } })
  }, [game?._id, router, dismissQuickGameOver])

  const handleQuickGameOverClose = useCallback(() => {
    const shouldGoHome = wasRemovedFromGame
    dismissQuickGameOver()
    if (shouldGoHome) {
      router.dismissTo('/(root)/(tabs)/home')
      return
    }
    if (!game?._id) return
    router.replace({ pathname: '/game/statistics', params: { gameId: game._id } })
  }, [dismissQuickGameOver, wasRemovedFromGame, game?._id, router])

  const buyChipConfirm: ConfirmOption = {
    text: translate('common.yesContinue'),
    onPress: handleRebuyAndResume
  }
  const buyChipCancel: ConfirmOption = {
    text: translate('common.noThanks'),
    onPress: handleDeclineAndExit
  }

  const endPracticeConfirm: ConfirmOption = {
    text: translate('common.yes'),
    onPress: handleContinueAfterWin
  }
  const endPracticeCancel: ConfirmOption = {
    text: translate('common.noThanks'),
    onPress: handleDeclineAndExit
  }

  const midTableRebuyConfirm: ConfirmOption = {
    text: translate('common.yesContinue'),
    onPress: handleMidTableRebuyAccept
  }
  const midTableRebuyCancel: ConfirmOption = {
    text: translate('common.noThanks'),
    onPress: handleMidTableRebuyDecline
  }

  // --- Render: single stable tree, all modals controlled via `visible` ---
  return (
    <>
      {/* biome-ignore format: for readability */}
      <SittingOutModal
        visible={!!showSittingOut}
        onReturn={handleReturnToGame}
        onLeave={handleLeaveFromSittingOut}
      />
      {/* biome-ignore format: for readability */}
      <GameModal visible={showDaily10Results} onClose={() => router.dismissTo('/(root)/(tabs)/play')}>
        <Daily10Results
          finalChips={daily10Status?.status === 'completed' ? daily10Status.result.finalChips : (myPlayer?.chips ?? 0)}
          countedHands={game?.countedHands ?? 0}
          preflopFoldsUsed={game?.freePreflopFoldsUsed ?? 0}
          preflopFoldsAllowed={game?.freePreflopFoldsAllowed ?? DEFAULT_FREE_PREFLOP_FOLDS}
          handOutcomes={game?.handOutcomes ?? []}
          playedDate={game?.playedDate ?? ''}
          streak={streak}
          onClose={() => router.dismissTo('/(root)/(tabs)/play')}
        />
      </GameModal>
      {/* biome-ignore format: for readability */}
      <BuyChip
        visible={showBuyChip}
        onClose={handleDeclineAndExit}
        confirm={buyChipConfirm}
        cancel={buyChipCancel}
      />
      {/* biome-ignore format: for readability */}
      <BuyChip
        visible={!!showMidTableRebuy}
        onClose={handleMidTableRebuyDecline}
        confirm={midTableRebuyConfirm}
        cancel={midTableRebuyCancel}
      />
      {/* biome-ignore format: for readability */}
      <EndPracticeGame
        visible={showEndPractice}
        onClose={handleDeclineAndExit}
        confirm={endPracticeConfirm}
        cancel={endPracticeCancel}
      />
      {/* biome-ignore format: for readability */}
      <QuickGameOverModal
        visible={showQuickGameOver}
        onClose={handleQuickGameOverClose}
        onViewStats={handleQuickGameOverViewStats}
        exitDestination={wasRemovedFromGame ? 'home' : undefined}
      />
      {/* biome-ignore format: for readability */}
      <TournamentGameOverModal
        visible={tournamentGameOverVisible}
        onClose={handleTournamentGameOver}
        onReturnToLobby={handleTournamentGameOver}
        title={tournamentEndDetails.title}
        message={tournamentEndDetails.message}
        buttonLabel={tournamentEndDetails.buttonLabel}
      />
    </>
  )
}
